import { Alert, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { AppError } from '../../api/contract';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { partnerCreditRepositoryToken } from './repository';

export function PartnerCreditVersionDetailPage({
  dataSource,
}: {
  readonly dataSource: DataSource;
}) {
  const { partnerId = '', versionId = '' } = useParams();
  const repository = useRepository(partnerCreditRepositoryToken);
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    async (signal) => {
      const versions = await repository.listVersions(partnerId, signal);
      return versions.find((version) => version.id === versionId) ?? null;
    },
    [partnerId, refreshKey, repository, versionId],
  );
  const error = safeError(resource.error, t('partnerCredit.errors.loadVersion'));
  return (
    <Stack spacing={2}>
      <DetailHeader
        backTo={`${paths.partnerCredit}/${encodeURIComponent(partnerId)}`}
        dataSource={dataSource}
        subtitle={partnerId}
        title={
          resource.data === null ? t('partnerCredit.versionTitle') : `v${resource.data.version}`
        }
      />
      <PageState
        emptyMessage={t('partnerCredit.versionNotFound')}
        error={error}
        onRetry={() => setRefreshKey((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <>
            <Paper component="section" sx={{ p: 2 }} variant="outlined">
              <Typography component="h2" variant="h6">
                {t('partnerCredit.sections.publicConfiguration')}
              </Typography>
              <MetadataList
                items={[
                  { label: t('partnerCredit.fields.id'), value: resource.data.id },
                  {
                    label: t('partnerCredit.fields.name'),
                    value: resource.data.configuration.name,
                  },
                  {
                    label: t('partnerCredit.fields.authType'),
                    value: resource.data.configuration.authType,
                  },
                  {
                    label: t('partnerCredit.fields.keyReference'),
                    value: resource.data.configuration.hmacKeyId ?? t('common.notAvailable'),
                  },
                  {
                    label: t('partnerCredit.fields.creditEndpoint'),
                    value: resource.data.configuration.creditEndpoint,
                  },
                  {
                    label: t('partnerCredit.fields.statusEndpoint'),
                    value: resource.data.configuration.statusEndpoint,
                  },
                  {
                    label: t('partnerCredit.fields.healthEndpoint'),
                    value: resource.data.configuration.healthEndpoint,
                  },
                  {
                    label: t('partnerCredit.fields.transactionResultEndpoint'),
                    value:
                      resource.data.configuration.transactionResultEndpoint ??
                      t('common.notAvailable'),
                  },
                  {
                    label: t('partnerCredit.fields.certifiedAt'),
                    value: time(resource.data.certifiedAt, language, t('common.notAvailable')),
                  },
                  {
                    label: t('partnerCredit.fields.publishedAt'),
                    value: time(resource.data.publishedAt, language, t('common.notAvailable')),
                  },
                ]}
              />
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('partnerCredit.secretOmitted')}
              </Alert>
            </Paper>
          </>
        )}
      </PageState>
    </Stack>
  );
}

function time(value: string | null, language: TimeLanguage, empty: string) {
  if (value === null) return empty;
  const formatted = formatTimestamp(value, 'Asia/Bangkok', language);
  return `${formatted.display} (${formatted.originalOffset})`;
}

function safeError(
  cause: unknown,
  fallback: string,
): Pick<AppError, 'message' | 'requestId'> | null {
  if (cause === null) return null;
  const requestId =
    typeof cause === 'object' && cause !== null && 'requestId' in cause
      ? (cause as { readonly requestId?: unknown }).requestId
      : null;
  return { message: fallback, requestId: typeof requestId === 'string' ? requestId : null };
}
